import { AgentAction } from '../components/chat/chat.ts';

export class AgentService {
  private static instance: AgentService;
  private actionListeners: Map<string, (action: AgentAction) => void> = new Map();

  static getInstance(): AgentService {
    if (!AgentService.instance) {
      AgentService.instance = new AgentService();
    }
    return AgentService.instance;
  }

  // Register listeners for UI updates
  onAction(type: string, callback: (action: AgentAction) => void) {
    this.actionListeners.set(type, callback);
  }

  // Execute an agent action (UI updates only, backend handles data operations)
  async executeAction(action: AgentAction): Promise<boolean> {
    try {
      console.log('Processing agent action for UI updates:', action);

      // Only handle UI updates and notifications
      const listener = this.actionListeners.get(action.type);
      if (listener) {
        listener(action);
      }

      // Handle navigation separately since it doesn't require backend calls
      if (action.type === 'navigation' && action.data.route) {
        window.location.hash = action.data.route;
      }

      return true;
    } catch (error) {
      console.error('Error processing agent action:', error);
      return false;
    }
  }

  // Parse natural language into actions
  parseMessageForActions(message: string): AgentAction[] {
    const actions: AgentAction[] = [];
    const lowerMessage = message.toLowerCase();

    // Calendar/scheduling patterns
    if (lowerMessage.includes('schedule') || lowerMessage.includes('appointment') || lowerMessage.includes('visit')) {
      const action = this.parseSchedulingMessage(message);
      if (action) actions.push(action);
    }

    // Task creation patterns
    if (lowerMessage.includes('remind me') || lowerMessage.includes('need to') || lowerMessage.includes('don\'t forget')) {
      const action = this.parseTaskMessage(message);
      if (action) actions.push(action);
    }

    // Budget patterns
    if (lowerMessage.includes('budget') || lowerMessage.includes('cost') || lowerMessage.includes('spend')) {
      const action = this.parseBudgetMessage(message);
      if (action) actions.push(action);
    }

    // Navigation patterns
    if (lowerMessage.includes('show me') || lowerMessage.includes('go to') || lowerMessage.includes('open')) {
      const action = this.parseNavigationMessage(message);
      if (action) actions.push(action);
    }

    return actions;
  }

  private parseSchedulingMessage(message: string): AgentAction | null {
    // Simple pattern matching for scheduling
    const timeRegex = /(\d{1,2}):?(\d{2})?\s*(am|pm)?/i;
    const dateRegex = /(next\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday|tomorrow|today)/i;
    // Improved venue regex to capture full venue names
    const venueRegex = /at\s+((?:the\s+)?[^,\n]+?)(?:\s+at\s+\d|$)/i;

    const timeMatch = message.match(timeRegex);
    const dateMatch = message.match(dateRegex);
    const venueMatch = message.match(venueRegex);

    if (timeMatch || dateMatch) {
      // Extract venue name more carefully
      let venueName = '';
      if (venueMatch) {
        venueName = venueMatch[1].trim();
        // Remove any trailing time references
        venueName = venueName.replace(/\s+\d{1,2}:?\d{0,2}\s*(am|pm)?$/i, '').trim();
        // Clean up common patterns
        venueName = venueName.replace(/\s+at\s*$/, '').trim();
      }

      return {
        type: 'calendar',
        data: {
          operation: 'add',
          title: venueName ? `Venue visit - ${venueName}` : 'Venue visit',
          date: dateMatch ? this.parseDate(dateMatch[0]) : new Date(),
          time: timeMatch ? `${timeMatch[1]}:${timeMatch[2] || '00'} ${timeMatch[3] || 'PM'}` : '2:00 PM',
          description: `Scheduled via chat: ${message}`,
          category: 'venue',
          location: venueName || 'Venue Location'
        }
      };
    }

    return null;
  }

  private parseTaskMessage(message: string): AgentAction | null {
    return {
      type: 'task',
      data: {
        operation: 'add',
        title: this.extractTaskTitle(message),
        description: message,
        category: 'planning',
        priority: 'MEDIUM'
      }
    };
  }

  private parseBudgetMessage(message: string): AgentAction | null {
    const amountRegex = /\$?(\d+(?:,\d{3})*(?:\.\d{2})?)/;
    const amountMatch = message.match(amountRegex);

    if (amountMatch) {
      return {
        type: 'budget',
        data: {
          operation: 'update',
          category: 'venue', // Default category, could be more intelligent
          amount: parseFloat(amountMatch[1].replace(',', ''))
        }
      };
    }

    return null;
  }

  private parseNavigationMessage(message: string): AgentAction | null {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('calendar') || lowerMessage.includes('schedule')) {
      return {
        type: 'navigation',
        data: { route: '/plan?tab=calendar' }
      };
    }
    
    if (lowerMessage.includes('budget') || lowerMessage.includes('money')) {
      return {
        type: 'navigation',
        data: { route: '/plan?tab=budget' }
      };
    }
    
    if (lowerMessage.includes('task') || lowerMessage.includes('todo')) {
      return {
        type: 'navigation',
        data: { route: '/plan?tab=task-board' }
      };
    }

    return null;
  }

  private parseDate(dateString: string): Date {
    const today = new Date();
    const lowerDate = dateString.toLowerCase();

    if (lowerDate.includes('today')) {
      return today;
    }

    if (lowerDate.includes('tomorrow')) {
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return tomorrow;
    }

    if (lowerDate.includes('friday')) {
      const friday = new Date(today);
      const daysUntilFriday = (5 - today.getDay() + 7) % 7;
      friday.setDate(today.getDate() + (daysUntilFriday || 7));
      return friday;
    }

    // Add more date parsing logic as needed
    return today;
  }

  private extractTaskTitle(message: string): string {
    // Simple extraction of task title from message
    const patterns = [
      /remind me to (.+)/i,
      /need to (.+)/i,
      /don't forget to (.+)/i,
      /make sure to (.+)/i
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match) {
        return match[1].trim();
      }
    }

    return message.length > 50 ? message.substring(0, 50) + '...' : message;
  }
} 